import React, { useRef } from 'react';
import { toast } from 'react-toastify';

const PhotoUpload = ({ 
  photos = [], 
  onPhotosChange, 
  mandatory = false,
  label = 'Photos',
  showError = false,
  disabled = false,
}) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/gif', 'image/png'];
    const maxSize = 25 * 1024 * 1024; // 25MB

    const validFiles = [];
    const invalidFiles = [];

    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        invalidFiles.push(`${file.name} is not a valid image`);
        continue;
      }
      if (file.size > maxSize) {
        invalidFiles.push(`${file.name} exceeds 25MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (invalidFiles.length > 0) {
      invalidFiles.forEach(msg => toast.error(msg));
    }

    if (validFiles.length) {
      const newPhotos = validFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));
      onPhotosChange && onPhotosChange([...photos, ...newPhotos]);
    }

    // Allow selecting same file again
    e.target.value = '';
  };

  const handleRemove = (index) => {
    const photo = photos[index];
    // Revoke object URL to free memory
    if (photo.preview && photo.preview.startsWith('blob:')) {
      URL.revokeObjectURL(photo.preview);
    }
    const updatedPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange && onPhotosChange(updatedPhotos);
  };

  return (
    <div className="photo-upload" style={{ marginBottom: '16px' }}>
      <label style={{ 
        display: 'block', 
        marginBottom: '8px', 
        fontSize: '13px', 
        fontWeight: 500,
        color: '#4a5568'
      }}>
        {label} {mandatory && <span style={{ color: '#4a5568' }}>*</span>}
      </label>
      
      {photos.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: '12px',
            marginBottom: '12px'
          }}
        >
          {photos.map((photo, index) => (
            <div
              key={index}
              style={{
                position: 'relative',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                padding: '4px',
                background: '#fff'
              }}
            >
              <img
                src={photo.preview}
                alt={`Photo ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100px',
                  objectFit: 'cover',
                  borderRadius: '4px'
                }}
              />
              <button
                type="button"
                onClick={() => handleRemove(index)}
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  background: '#fed7d7',
                  border: 'none',
                  borderRadius: '50%',
                  width: '22px',
                  height: '22px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  color: '#c53030',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/gif,image/png"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <button
        type="button"
        onClick={() => !disabled && fileInputRef.current?.click()}
        disabled={disabled}
        style={{
          padding: '10px 16px',
          background: disabled ? '#edf2f7' : '#f7fafc',
          border: '2px solid #e2e8f0',
          borderRadius: '6px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '13px',
          fontWeight: 500,
          color: disabled ? '#a0aec0' : '#4a5568',
          width: '100%',
          opacity: disabled ? 0.6 : 1
        }}
      >
        {photos.length > 0
          ? `Add Photos (${photos.length} selected)`
          : 'Add Photos'}
      </button>
      
      {disabled && (
        <small style={{ color: '#718096', fontSize: '11px', marginTop: '4px', display: 'block' }}>
          Photo upload is only available when selecting a single asset. To track asset history and view uploaded images in the history report, please select only one asset.
        </small>
      )}
      
      {!disabled && mandatory && showError && photos.length === 0 && (
        <small style={{ color: '#e53e3e', fontSize: '11px', marginTop: '4px', display: 'block' }}>
          At least one photo is required
        </small>
      )}
    </div>
  );
};

export default PhotoUpload;

